import React from "react";
import { flushSync } from "react-dom";
import styled from "styled-components";
import SolitaireCore from "../SolitaireCore";
import {
  GhostCards,
  DRAG_THRESHOLD_PX,
  cardsForSource,
  sourceElements,
  hideElements,
  restoreElements,
  hitTestCard,
} from "../SolitaireCore/dragKit";
import { CARD_BACKS } from "../Tile/tiles";
import { MOVE_TYPE, isWon, spriteKey } from "../../engine/solitaire";
import { cardPlace } from "../../sound/sound";

// Pick a card back deterministically from gameId so every neighbor has a
// visibly different deck. Knuth hash → index into CARD_BACKS.
export function cardBackForGameId(gameId) {
  const h = ((gameId + 1) * 2654435761) >>> 0;
  return CARD_BACKS[h % CARD_BACKS.length];
}

// How far (window px) a remote press may land from any card and still
// count as grabbing it. Humans are pixel-exact (same 620x435 space both
// ends); bots aim with jitter — a near-miss snap reads better than a
// hand dragging nothing.
const REMOTE_HIT_RADIUS = 20;

// Remote playback runs through a fixed-delay line (entity
// interpolation): every subscription event — cursor frames, moves,
// undos, terminal — is stamped `arrival + delayMs` and processed in
// order when due, so the whole performance (hand, cards, sounds)
// replays at its true recorded pace, a constant beat late. The drawn
// arrow interpolates between the last processed cursor sample and the
// next queued one: velocity is the player's own, lag is bounded by the
// delay and never accumulates. Press/release edges and the board move
// ride the same stamped timeline, so the hand is AT the card when the
// card moves, by construction. Ghost games need no special casing: the
// wire hides ghost identity and the server paces their replay, so the
// same buffer smooths both.
//
// The wire carries ~12Hz samples with no timestamps; the write pump may
// coalesce frames into one burst. Stamping re-spreads burst-arrived
// motion frames toward the nominal cadence (bounded by PACE_SLACK_MS so
// delay can't creep).
//
// Slowing things down happens HERE, at stamping, never at drawing: a
// motion frame that covers more distance than maxSpeed allows gets its
// stamp pushed later, and every subsequent event clamps to the same
// monotonic timeline, so the drop, the click, and the board change all
// wait for the hand.
//
// Delay depth and speed ceiling vary per game id (deterministic,
// stable per game) and every viewer of a given game shares them — the
// playstyle itself is the player's own recorded motion.
function handFeel(gameId) {
  const h = ((gameId + 1) * 0x9e3779b1) >>> 0;
  const u = (h & 0xffff) / 0xffff;
  const v = ((h >>> 16) & 0xffff) / 0xffff;
  return {
    delayMs: 160 + u * 60, // constant playback delay, 160–220ms
    // Hands never appear to move faster than this (window-local px/s).
    // Enforced by time-stretch at stamping, so raising/lowering it
    // slows or speeds the WHOLE performance in sync — this is the
    // "everything a little slower/faster" knob.
    maxSpeed: 550 + v * 200, // 550–750 px/s
  };
}

// Server-side cursor relay cadence is ~12Hz (~85ms). Motion frames
// that arrive closer together than the cadence allows were coalesced
// in transit — re-space them at stamping so the hand glides through
// instead of teleporting…
const BURST_MIN_GAP_MS = 40;
// …but never let re-spacing or speed-limit stretching push a stamp
// more than this past its nominal `arrival + delayMs` slot. Bounds
// the total delay under sustained fast motion; the buffer recovers
// during the player's pauses (solitaire is mostly pauses).
const PACE_SLACK_MS = 350;
// Non-motion events (press/release edges, moves, undos, endings)
// stamped within this window of the previous stamp share it EXACTLY.
// The release edge and its move arrive in one task (PROTOCOL.md §4);
// sharing a stamp keeps them in one drain pass. This is HALF of the
// no-drop-flicker guarantee — the other half is the flushSync around
// the drain (see tick), which commits that pass's React updates
// before the frame paints. Do not remove either without restoring
// the guarantee some other way.
const STAMP_MERGE_MS = 8;
// An ARRIVAL gap between cursor samples longer than this is a real
// pause, not the frame cadence: hold the hand, then close the
// distance with an eased reach (duration scaled to the distance at
// the hand's speed ceiling) instead of drifting across the silence.
const PAUSE_GAP_MS = 250;
const REACH_MIN_MS = 140;
const REACH_MAX_MS = 600;
// Smoothstep's peak velocity is 1.5x its average: pad the reach
// duration by the same factor so even mid-reach the hand honors the
// game's speed ceiling (very long reaches cap at REACH_MAX_MS and may
// briefly exceed it — a decisive move after a think).
const REACH_PEAK_FACTOR = 1.5;

const clampGhost = (v, lo, hi) =>
  Math.min(Math.max(v, lo), Math.max(lo, hi));

// Someone else's game, watched over the wire. Pure subscriber — engine
// state is owned by useSolitaireServer, which reduces incoming moves once
// and fans them out here.
//
// The wire carries the player's cursor with its button state, and
// press/release edges are never dropped or throttled (server/PROTOCOL.md).
// This component replays those frames
// through the same drag-presentation kit the player's own board uses —
// hit-test the press against the rendered cards, lift them into a ghost
// that rides the remote cursor, drop on release. The move itself arrives
// as its own message right after the release edge (§4 ordering), so the
// board snaps to truth the moment the hand lets go; if no move follows,
// the drag was abandoned and the restored cards already ARE the truth.
// Click-without-drag shows the selection wash, exactly like at home.
function SpectatedSolitaire({
  title,
  gameId,
  subscribeGame,
  // Machine identity generation from the hook: bumps when this id's
  // machine was rebuilt from scratch (ghost replays reuse historical
  // numbers). Re-runs the subscription effect so a frozen terminal
  // instance re-arms instead of silently wearing a different game's id.
  generation = 0,
  // Injected by the Stage (desktop shell): focus, title-bar buttons,
  // and the Help → About hook.
  inactive = true,
  onControlClick,
  onHelpAbout,
  ambientSound = true,
  // Fired when a won game's cascade has fully played out here — the
  // Desktop greys only maximize; compact also greys minimize (the
  // Other Games grid has nowhere to minimize to).
  disabledControls = ["maximize"],
  // Desktop windows only: fired when this game's departure has fully
  // played (the close ritual for a loss/abandon, the cascade for a
  // win). The shell then closes the window; the server redeals the
  // seat after its beat. Browse-grid slots omit it and keep their
  // ended-stub behavior.
  onDeparted = null,
}) {
  const rootRef = React.useRef(null);
  const [state, setState] = React.useState(null);
  // The player's mouse inside their own window (their screen, their
  // cursor). Live-only presence: hidden when they leave the window or
  // go quiet for a while.
  const [cursor, setCursor] = React.useState(null);
  // Click-select mirrored from their press/release (cleared by the next
  // press or by any move — same lifecycle as the real selection).
  const [selection, setSelection] = React.useState(null);
  // {cards, offX, offY} while their drag is in flight; position derives
  // from the interpolated cursor, so ghost and arrow travel together.
  const [lift, setLift] = React.useState(null);
  // {stacks, startedAtMs} while THEIR win cascade plays (the hook keeps
  // a won neighbor mounted until we report the cascade done, so watchers
  // get the whole show). stacks: foundations copies the cascade drains;
  // startedAtMs: performance.now-domain epoch of the win — witnessed
  // live it's "now", but a window mounted mid-show (All Active Games
  // opened during the cascade) sets it in the past so WinCelebration
  // fast-forwards to the frame every other viewer is on.
  const [celebration, setCelebration] = React.useState(null);

  const stateRef = React.useRef(null);
  stateRef.current = state;
  // Live press being tracked: {x, y, src, grabEl, dragging, hidden[]}.
  const pressRef = React.useRef(null);
  const downRef = React.useRef(false);
  // Once a game ends, late frames are noise: live cursor updates during
  // the terminal beat could otherwise make a frozen final board look live.
  const terminalRef = React.useRef(false);
  // The close ritual (terminal desktop windows): timers + one-shot guard.
  const closingRef = React.useRef(false);
  const closeTimerRef = React.useRef(null);
  // Last live hand position, so the ritual's walk starts from wherever
  // the player's cursor actually was.
  const lastCursorRef = React.useRef(null);

  // The delay line (see the block comment above handFeel): stamped
  // items pending processing, in stamp order. Wire items are
  // {at, event}; the close ritual pushes synthetic {at, ritual:true,
  // x, y, down} samples through the same pipe so it glides too.
  const queueRef = React.useRef([]);
  // Monotonic stamping state: the last stamp handed out, and the down
  // state as of the last stamped cursor frame (press/release edges are
  // order-clamped but never re-spaced — edges must not drift).
  const lastStampRef = React.useRef(0);
  const stampDownRef = React.useRef(false);
  // The drawn hand: prevSampleRef is the last PROCESSED cursor sample
  // ({at, x, y} — the interpolation segment's start), drawnRef the
  // position actually painted this frame. The rAF loop writes straight
  // to the arrow/ghost DOM nodes so 60fps motion never re-renders React.
  const prevSampleRef = React.useRef(null);
  const drawnRef = React.useRef(null);
  const arrowElRef = React.useRef(null);
  const ghostElRef = React.useRef(null);
  const liftRef = React.useRef(null);
  liftRef.current = lift;

  // Hand hide (and the won/celebration paths) go through here; showing
  // happens in processItem, where the stamped time is known.
  const setHand = (p) => {
    if (!p) {
      prevSampleRef.current = null;
      drawnRef.current = null;
    }
    setCursor(p);
  };

  // Paint the hand (and any lifted cards riding it) at a window-local
  // point. The single writer for arrow/ghost position.
  const drawHand = (x, y) => {
    drawnRef.current = { x, y };
    const arrowEl = arrowElRef.current;
    if (arrowEl) {
      arrowEl.style.setProperty("--rx", x + "px");
      arrowEl.style.setProperty("--ry", y + "px");
    }
    const l = liftRef.current;
    const ghostEl = ghostElRef.current;
    if (l && ghostEl) {
      const { gx, gy } = ghostXY({ x, y }, l);
      ghostEl.style.setProperty("--gx", gx + "px");
      ghostEl.style.setProperty("--gy", gy + "px");
    }
  };

  // Window-space (620x435) → client px, via the rendered rect (handles
  // the scaled-down copies: mobile Other Games, All Active Games).
  const toClient = (x, y) => {
    const root = rootRef.current;
    if (!root) return null;
    const r = root.getBoundingClientRect();
    if (r.width === 0) return null;
    const scale = r.width / 620;
    return { x: r.left + x * scale, y: r.top + y * scale, rect: r, scale };
  };

  const dropLift = () => {
    const p = pressRef.current;
    if (p && p.hidden.length) restoreElements(p.hidden);
    pressRef.current = null;
    setLift(null);
  };

  // Lifted cards ride the hand, confined to the felt (top card clamped
  // fully inside). Shared by the React render (mount position) and the
  // rAF follower (motion) so both draw from the same math.
  const ghostXY = (pt, l) => ({
    gx:
      clampGhost(pt.x - l.offX, l.felt.x, l.felt.x + l.felt.w - l.cardW) -
      l.felt.x,
    gy:
      clampGhost(pt.y - l.offY, l.felt.y, l.felt.y + l.felt.h - l.cardH) -
      l.felt.y,
  });

  // The departure ceremony: the ghost hand walks up to the titlebar X
  // and clicks it, then the window closes and the server redeals the
  // seat after its beat. Clearer than a blink-out — "they closed their
  // game."
  const beginCloseRitual = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    dropLift();
    setSelection(null);
    // Start from wherever the hand is actually DRAWN right now (it may
    // be mid-glide behind the last raw frame), else its last known
    // spot, else mid-window.
    const from =
      drawnRef.current ?? lastCursorRef.current ?? { x: 310, y: 240 };
    const to = { x: 601, y: 12 }; // the Close button, window-local px
    // Hidden tab: the rAF loop that walks the queue is parked and no
    // further wire events will arrive (the game is over), so queued
    // waypoints would stall forever. Nobody's watching — skip the walk
    // and close after a beat.
    if (document.hidden) {
      queueRef.current.length = 0;
      closeTimerRef.current = setTimeout(() => {
        if (onDeparted) onDeparted();
      }, 1000);
      return;
    }
    // The walk rides the same delay-line queue as live frames: synthetic
    // smoothstepped waypoints, glided between by the interpolator. Any
    // undelivered live items are moot — the game is over.
    const q = queueRef.current;
    q.length = 0;
    const now = performance.now();
    prevSampleRef.current = { at: now, x: from.x, y: from.y };
    setCursor({ x: from.x, y: from.y, down: false });
    const STEPS = 6;
    for (let i = 1; i <= STEPS; i++) {
      const t = i / STEPS;
      const k = t * t * (3 - 2 * t); // smoothstep: settle, don't slam
      q.push({
        at: now + i * 130,
        ritual: true,
        x: from.x + (to.x - from.x) * k,
        y: from.y + (to.y - from.y) * k,
        down: false,
      });
    }
    // The click on the X, held long enough to read (processItem fires
    // onDeparted 450ms after this sample lands), then the window goes.
    q.push({
      at: now + STEPS * 130 + 170,
      ritual: true,
      x: to.x,
      y: to.y,
      down: true,
      press: true,
    });
  };

  React.useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const onCursorFrame = (c) => {
    const wasDown = downRef.current;
    downRef.current = c.down;

    if (c.down && !wasDown) {
      // Press edge: a new press always re-evaluates the selection (and
      // settles any ghost still waiting out its post-release hold).
      dropLift();
      setSelection(null);
      const pt = toClient(c.x, c.y);
      const hit =
        pt &&
        hitTestCard(rootRef.current, pt.x, pt.y, REMOTE_HIT_RADIUS * pt.scale);
      pressRef.current = hit
        ? { x: c.x, y: c.y, src: hit.src, grabEl: hit.el, dragging: false, hidden: [] }
        : null;
      return;
    }

    if (!c.down && wasDown) {
      // Release edge. A release that completed a MOVE arrives embedded
      // in the move message (c on the move frame — PROTOCOL.md §4), so
      // this handler and the board update run in the same task → one
      // commit → no origin-flash. A BARE release edge therefore means
      // the drag was abandoned: snap home immediately.
      const p = pressRef.current;
      if (p) {
        if (p.dragging) dropLift();
        else {
          pressRef.current = null;
          setSelection(p.src);
        }
      }
      return;
    }

    // Motion while held: crossing the threshold lifts the cards.
    const p = pressRef.current;
    if (!c.down || !p || p.dragging) return;
    if (Math.hypot(c.x - p.x, c.y - p.y) < DRAG_THRESHOLD_PX) return;
    const pt = toClient(0, 0);
    const s = stateRef.current;
    if (!pt || !s) return;
    const er = p.grabEl.getBoundingClientRect();
    p.dragging = true;
    // Felt rect + card size in window-local px: the ghost is confined
    // to the felt exactly like the dragger's own view (top card clamped
    // fully inside; a run's fanned tail clips at the felt edge).
    const feltEl = rootRef.current.querySelector("[data-felt]");
    const fr = (feltEl ?? rootRef.current).getBoundingClientRect();
    // Ghost first, hide second — same anti-flicker order as the real
    // drag (this runs from a websocket message, another native context
    // where React's flush can miss a paint).
    flushSync(() => {
      setLift({
        cards: cardsForSource(s, p.src),
        offX: p.x - (er.left - pt.rect.left) / pt.scale,
        offY: p.y - (er.top - pt.rect.top) / pt.scale,
        felt: {
          x: (fr.left - pt.rect.left) / pt.scale,
          y: (fr.top - pt.rect.top) / pt.scale,
          w: fr.width / pt.scale,
          h: fr.height / pt.scale,
        },
        cardW: er.width / pt.scale,
        cardH: er.height / pt.scale,
      });
    });
    p.hidden = sourceElements(rootRef.current, p.src, p.grabEl);
    hideElements(p.hidden);
  };

  // The live subscribe handle, kept for onCascadeDone: a finished
  // cascade reports back so a browse-grid celebration's deferred
  // teardown can land (no-op for watched desktop windows).
  const handleRef = React.useRef(null);

  React.useEffect(() => {
    terminalRef.current = false;
    closingRef.current = false;
    clearTimeout(closeTimerRef.current);
    queueRef.current = [];
    lastStampRef.current = 0;
    stampDownRef.current = false;
    prevSampleRef.current = null;
    drawnRef.current = null;
    const feel = handFeel(gameId);
    // They WON. Play their cascade in this window — the piece's one
    // gift, witnessed from the next table over. A real win has full
    // foundations; the ?spectatewin dev knob fakes the ending on a
    // mid-game board, so synthesize a full set there (mirrors the
    // ?celebrate knob on the player's own board). elapsedMs > 0 means
    // we're joining a show already in flight.
    const startCelebration = (board, elapsedMs) => {
      const s = board;
      const founds =
        s && isWon(s)
          ? s.foundations.map((f) => f.slice())
          : [0, 1, 2, 3].map((suit) =>
              Array.from({ length: 13 }, (_, rank) => suit * 13 + rank),
            );
      setCelebration({
        stacks: founds,
        startedAtMs: performance.now() - elapsedMs,
      });
    };
    // Process one due item from the delay line. Everything a spectator
    // sees or hears happens here, at the item's stamped time.
    const processItem = (item) => {
      if (item.ritual) {
        // Synthetic close-ritual waypoint: position only (the queue
        // was cleared of wire items when the ritual began).
        prevSampleRef.current = { at: item.at, x: item.x, y: item.y };
        setCursor({ x: item.x, y: item.y, down: item.down });
        if (item.press) {
          // The click on the X, held long enough to read, then the
          // window actually goes.
          closeTimerRef.current = setTimeout(() => {
            if (onDeparted) onDeparted();
          }, 450);
        }
        return;
      }
      const event = item.event;
      if (terminalRef.current) return;
      if (event.cursor) {
        if (event.cursor.x < 0) {
          // Their hand left the window (the leave frame carries
          // down=false, so any drag already dropped above).
          onCursorFrame({ x: 0, y: 0, down: false });
          lastCursorRef.current = null;
          setHand(null);
        } else {
          onCursorFrame(event.cursor);
          lastCursorRef.current = event.cursor;
          prevSampleRef.current = {
            at: item.at,
            x: event.cursor.x,
            y: event.cursor.y,
          };
          // A hand APPEARING snaps to its first sample — never a
          // glide in from stale coordinates.
          if (!drawnRef.current) drawHand(event.cursor.x, event.cursor.y);
          setCursor(event.cursor);
        }
        return;
      }
      if (event.terminal) {
        terminalRef.current = true;
        dropLift();
        setSelection(null);
        if (event.reason === "won") {
          // Their hand leaves the frame for the show — a cursor parked
          // over a cascade reads as a stuck spectator, not a winner.
          downRef.current = false;
          setHand(null);
          // event.curState, NOT stateRef: the machine applied the
          // winning move synchronously, but the write pump coalesces
          // that move with game_ended into one frame batch — React
          // hasn't re-rendered between them, so stateRef still holds
          // the pre-win board, isWon() would fail, and the spectator's
          // cascade would fall back to canonical suit-ordered
          // foundations instead of the winner's real ace order.
          startCelebration(
            event.curState ?? stateRef.current,
            event.wonElapsedMs ?? 0,
          );
        } else if (event.reason && onDeparted) {
          // A desktop window: they left, so their hand closes the
          // window (onDeparted fires when the press lands).
          downRef.current = false;
          beginCloseRitual();
        } else {
          downRef.current = false;
          setHand(null);
        }
        return;
      }
      // A move/undo means the world changed: any in-flight drag is
      // over (restore BEFORE the re-render reuses those nodes) and the
      // selection it came from is stale.
      dropLift();
      setSelection(null);
      // Hands at nearby tables: a neighbor placing a card is just barely
      // audible. Draws stay silent — six tables of stock-flipping would
      // be patter, not ambience.
      if (ambientSound && event.move && event.move.type === MOVE_TYPE.MOVE) {
        cardPlace(0.12);
      }
      setState(event.curState);
    };

    // Stamp an arriving wire event into the delay line, preserving
    // arrival order (FIFO is a protocol invariant — never reorder).
    // Last stamped cursor position + arrival time: the speed-limit
    // stretch and the pause (reach) detection both key off arrivals,
    // not stamps. Reset on leave so nothing stretches or reaches
    // across a disappearance.
    let lastPos = null;
    let lastCursorArrival = -Infinity;
    const enqueue = (event) => {
      // Hidden tab: rAF is parked, so the queue would pile up while
      // the board froze. Nobody can see smoothness there — flush and
      // apply immediately instead.
      if (document.hidden) {
        const q = queueRef.current;
        while (q.length) processItem(q.shift());
        processItem({ at: performance.now(), event });
        return;
      }
      const now = performance.now();
      let at = now + feel.delayMs;
      const c = event.cursor;
      const isMotion = !!c && c.x >= 0 && c.down === stampDownRef.current;
      if (isMotion && lastPos) {
        // Pure motion frame (no press/release edge): re-space
        // burst-arrived frames toward the wire cadence, and stretch
        // time so the hand never covers ground faster than its speed
        // ceiling — everything downstream clamps to the same
        // timeline, so the whole performance slows together. Bounded
        // so total delay can't creep.
        const dist = Math.hypot(c.x - lastPos.x, c.y - lastPos.y);
        at = Math.max(
          at,
          lastStampRef.current + BURST_MIN_GAP_MS,
          lastStampRef.current + (dist / feel.maxSpeed) * 1000,
        );
        at = Math.min(at, now + feel.delayMs + PACE_SLACK_MS);
      }
      at = Math.max(at, lastStampRef.current);
      // Edges, moves, undos, endings: share the previous stamp when
      // they arrived on its heels (see STAMP_MERGE_MS — this is the
      // no-flicker guarantee for release + move).
      if (!isMotion && at - lastStampRef.current < STAMP_MERGE_MS) {
        at = lastStampRef.current;
      }
      const item = { at, event };
      if (c) {
        stampDownRef.current = c.x < 0 ? false : c.down;
        if (c.x >= 0) {
          // A long silence since the last sample ARRIVED is a real
          // pause — the interpolator holds, then reaches.
          item.reach = lastPos && now - lastCursorArrival > PAUSE_GAP_MS;
          lastPos = { x: c.x, y: c.y };
          lastCursorArrival = now;
        } else {
          lastPos = null;
        }
      }
      lastStampRef.current = at;
      queueRef.current.push(item);
    };

    const handle = subscribeGame(gameId, enqueue);
    setState(handle.state ?? null);

    // The playback loop: drain due items, then paint the hand at its
    // interpolated position along the stamped timeline — between the
    // last processed sample and the next queued one, so the drawn
    // arrow and the game logic share one clock and the hand is AT the
    // card when the card moves.
    let raf;
    const tick = (now) => {
      const q = queueRef.current;
      if (q.length && q[0].at <= now) {
        // flushSync, NOT a plain loop: processing pairs direct-DOM
        // mutations (hide/restore of the dragged cards) with React
        // state updates (ghost unmount, board change). We are inside
        // rAF — the browser paints immediately after this task, and
        // React's default flush from here is a scheduled macrotask
        // that runs only AFTER that paint. Without the sync commit,
        // every remote drop would paint one frame with the restored
        // cards back at their origin and the board not yet advanced.
        // With it, everything a drain does lands in a single paint.
        // STAMP_MERGE_MS is the
        // other half of the guarantee: it keeps a release edge and its
        // move in the same drain.
        flushSync(() => {
          while (q.length && q[0].at <= now) processItem(q.shift());
        });
      }
      const prev = prevSampleRef.current;
      if (prev) {
        let next = null;
        for (const it of q) {
          if (it.ritual) {
            next = it;
            break;
          }
          const c = it.event.cursor;
          if (c && c.x >= 0) {
            next = { at: it.at, x: c.x, y: c.y, reach: it.reach };
            break;
          }
          // A queued leave frame: hold — never glide toward a
          // position on the far side of a disappearance.
          if (c) break;
        }
        let x = prev.x;
        let y = prev.y;
        if (next && next.at > prev.at) {
          let start = prev.at;
          if (next.reach) {
            // After a real pause: hold, then close the distance with
            // an eased reach paced to the hand's speed ceiling.
            const dist = Math.hypot(next.x - prev.x, next.y - prev.y);
            const reachMs = Math.min(
              REACH_MAX_MS,
              Math.max(
                REACH_MIN_MS,
                (dist / feel.maxSpeed) * 1000 * REACH_PEAK_FACTOR,
              ),
            );
            start = Math.max(prev.at, next.at - reachMs);
          }
          let t = (now - start) / (next.at - start);
          t = Math.min(Math.max(t, 0), 1);
          if (next.reach) t = t * t * (3 - 2 * t);
          x = prev.x + (next.x - prev.x) * t;
          y = prev.y + (next.y - prev.y) * t;
        }
        // Parked hands write nothing — a room of settled mice costs
        // no style churn.
        const d = drawnRef.current;
        if (!d || d.x !== x || d.y !== y) drawHand(x, y);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Tab going hidden parks the rAF loop mid-queue: flush everything
    // due-or-not so the board stays truthful while nobody's watching
    // (new arrivals take the immediate path in enqueue).
    const onHide = () => {
      if (!document.hidden) return;
      const q = queueRef.current;
      while (q.length) processItem(q.shift());
    };
    document.addEventListener("visibilitychange", onHide);
    handleRef.current = handle;
    // Mounted mid-show: the terminal won event fired before this window
    // existed (All Active Games opened during a neighbor's cascade). The
    // handle carries how far the show has run — join it in progress.
    if (handle.celebrationElapsedMs != null && handle.state) {
      terminalRef.current = true;
      startCelebration(handle.state, handle.celebrationElapsedMs);
    }
    return () => {
      handleRef.current = null;
      handle.unsubscribe();
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onHide);
      queueRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, generation, subscribeGame, ambientSound]);

  // Quiet mice evaporate — nobody's hand hovers motionless for 8s. A
  // stale lift can't outlive its hand (belt-and-braces; the release
  // edge is must-deliver, but a severed player never sends one).
  React.useEffect(() => {
    // The ritual owns the hand once it starts — evaporation must not
    // blank it mid-walk or mid-press.
    if (!cursor || closingRef.current) return undefined;
    const t = setTimeout(() => {
      dropLift();
      downRef.current = false;
      setHand(null);
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  if (!state) return null;

  // While their cascade plays, render the popped-down foundation copies
  // so stacks shrink under the flying cards (same trick as the player's
  // own board). The rest of the board is bare felt: a real win emptied
  // it move by move, but the ?spectatewin knob fakes the ending on a
  // mid-game board, so wipe it explicitly either way.
  const displayState =
    celebration != null
      ? {
          ...state,
          foundations: celebration.stacks,
          tableau: state.tableau.map(() => ({ faceDown: [], faceUp: [] })),
          waste: [],
          stock: [],
        }
      : state;
  const onPopTop = (foundationIdx) => {
    setCelebration((prev) => {
      if (!prev) return prev;
      const stacks = prev.stacks.map((f) => f.slice());
      stacks[foundationIdx].pop();
      return { ...prev, stacks };
    });
  };
  const onCascadeDone = () => {
    setCelebration(null);
    if (handleRef.current?.cascadeDone) handleRef.current.cascadeDone();
    // A won desktop window departs once its show is over; the server
    // redeals the seat after the celebration window.
    if (onDeparted) onDeparted();
  };

  return (
    <Root ref={rootRef}>
      <SolitaireCore
      title={title}
      state={displayState}
      selection={selection}
      winCelebration={
        celebration != null
          ? {
              foundations: celebration.stacks.map((f) => f.map(spriteKey)),
              seed: gameId,
              startedAtMs: celebration.startedAtMs,
              onPopTop,
              onDone: onCascadeDone,
            }
          : null
      }
      // The real Solitaire application icon is shared by every game window.
      titleIcon="solitaire"
      cardBack={cardBackForGameId(gameId)}
      interactive={false}
      inactive={inactive}
      // Not your game: the Game menu is honestly grey. Help still works.
      menus={[
        { label: "Game", disabled: true },
        {
          label: "Help",
          items: [
            {
              label: "About Solitaire Alone Together…",
              onClick: onHelpAbout,
              disabled: !onHelpAbout,
            },
          ],
        },
      ]}
      // Minimize and close work on neighbors now (close = stop watching);
      // maximize stays honestly disabled everywhere.
      disabledControls={disabledControls}
      pressedControls={
        closingRef.current && cursor?.down ? ["close"] : undefined
      }
      onControlClick={onControlClick}
      />
      {lift &&
        cursor &&
        (() => {
          // Mount at the DRAWN position, not the raw frame — the rAF
          // loop owns motion from here; React re-renders (12Hz frames,
          // board updates) must re-assert the same point or the ghost
          // would flicker between interpolated and raw.
          const { gx, gy } = ghostXY(drawnRef.current ?? cursor, lift);
          return (
            <RemoteGhostClip
              aria-hidden="true"
              style={{
                left: lift.felt.x + "px",
                top: lift.felt.y + "px",
                width: lift.felt.w + "px",
                height: lift.felt.h + "px",
              }}
            >
              <RemoteGhost
                ref={ghostElRef}
                data-remote-ghost="true"
                style={{ "--gx": gx + "px", "--gy": gy + "px" }}
              >
                <GhostCards cards={lift.cards} />
              </RemoteGhost>
            </RemoteGhostClip>
          );
        })()}
      {cursor && (
        <RemoteCursor
          ref={arrowElRef}
          style={{
            "--rx": (drawnRef.current ?? cursor).x + "px",
            "--ry": (drawnRef.current ?? cursor).y + "px",
          }}
          data-pressed={cursor.down ? "true" : undefined}
          viewBox="0 0 12 19"
          aria-hidden="true"
        >
          <path
            d="M1 1 L1 15 L4.5 12 L7 18 L9.5 17 L7 11 L11 11 Z"
            fill="#c9c9c9"
            stroke="#000"
            strokeWidth="1"
          />
        </RemoteCursor>
      )}
    </Root>
  );
}

const Root = styled.div`
  position: relative;
  /* Size to the fixed 620x435 window inside, NOT the ambient width —
     as a plain block this clipped scaled-down copies (mobile Other
     Games, All Active Games) to their footprint width before the transform. */
  width: fit-content;
  height: fit-content;
  /* The remote arrow stops at the window edge, like a real screen. */
  overflow: hidden;
`;

// Their cards in flight, riding the SAME smoothed follower as the
// arrow, so hand and cards travel as one gesture. Below the arrow,
// above the board; confined to the felt exactly like the dragger's
// own view (clamped top card, clipped run tail).
const RemoteGhostClip = styled.div`
  position: absolute;
  overflow: hidden;
  z-index: 25;
  pointer-events: none;
`;

const RemoteGhost = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(var(--gx), var(--gy));
  /* No CSS transition: the rAF delay-line interpolator owns motion (a
     retargeted linear transition is exactly the sprint/stall this
     replaced). */
  pointer-events: none;
`;

// Their arrow, on their screen. The delay-line interpolator above glides
// it between ~12Hz frames so travel reads as motion, not teleports (see
// the block comment above handFeel); clipped by the window (overflow)
// like a real screen edge. Grey and translucent — a stranger's cursor
// on their own screen, seen through the glass (grey body over lowered
// opacity so it doesn't disappear into the felt). Presses shrink
// slightly so clicks and drags read as clicks and drags rather than a
// hovering arrow while cards mysteriously move.
const RemoteCursor = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  width: 15px;
  height: 24px;
  opacity: 0.9;
  transform: translate(var(--rx), var(--ry));
  z-index: 30;
  pointer-events: none;
  filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.35));

  &[data-pressed="true"] {
    transform: translate(var(--rx), var(--ry)) scale(0.92);
  }
`;

// Memoized: All Active Games re-renders on every scroll frame, and a
// dozen-plus full boards re-rendering per frame is the difference between
// smooth and janky scrolling on weak machines. Callers keep prop
// identities stable (module-const arrays, useCallback handlers) so the
// shallow compare actually holds.
export default React.memo(SpectatedSolitaire);
